<template>
  <div class="order-timeline-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <i class="el-icon-loading"></i>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-state">
      <i class="el-icon-error"></i>
      <p>加载失败</p>
      <el-button size="small" @click="fetchData">重新加载</el-button>
    </div>

    <!-- 正常状态 -->
    <template v-else>
      <!-- 空态 -->
      <div v-if="orderTimeList.length === 0 && repayTimeList.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>暂无时间线记录</p>
      </div>

      <template v-else>
        <!-- 订单操作记录 -->
        <div class="timeline-section">
          <div class="section-header">
            <div class="title-with-icon">
              <i class="icon el-icon-s-order"></i>
              <h5 class="section-title">订单操作记录</h5>
            </div>
            <span class="record-count">{{ orderTimeList.length }}条记录</span>
          </div>
          <!-- 在 section-header 后添加 -->
          <div v-if="currentStatus" class="custom-scroll-alert">
            <div class="alert-icon">
              <i class="el-icon-warning"></i>
            </div>
            <div class="scrolling-text-container">
              <div class="scrolling-text">{{ currentStatus }}</div>
            </div>
          </div>


          <div class="vertical-timeline">
            <div
                v-for="(item, index) in orderTimeList"
                :key="`order-${index}`"
                class="timeline-item"
                :class="{'first-item': index === 0}"
            >
              <div class="timeline-dot" :style="{backgroundColor: getColor(item)}"></div>
              <div class="timeline-content">
                <div class="timeline-time">{{ item.time }}</div>
                <div class="timeline-title">
                  <span class="operate-type">{{ item.operateType }}</span>
                  <span class="status-tag" :class="getStatusClass(item.operateType)">{{ item.operateType }}</span>
                </div>
                <div class="timeline-desc">
                  <span class="desc-text">{{ item.content }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 还款信息 -->
        <div v-if="repayTimeList.length > 0" class="timeline-section">
          <div class="section-header">
            <div class="title-with-icon">
              <i class="icon el-icon-money"></i>
              <h5 class="section-title">还款记录</h5>
            </div>
            <span class="record-count">{{ repayTimeList.length }}条记录</span>
          </div>

          <div class="vertical-timeline">
            <div
                v-for="(item, index) in repayTimeList"
                :key="`repay-${index}`"
                class="timeline-item"
                :class="{'first-item': index === 0}"
            >
              <div class="timeline-dot" :style="{backgroundColor: getRepayColor(item)}"></div>
              <div class="timeline-content">
                <div class="timeline-time">{{ item.time }}</div>
                <div class="timeline-title">
                  <span class="operate-type">{{ item.style }}</span>
                  <span class="status-tag" :class="getRepayStatusClass(item.style)">{{ item.style }}</span>
                </div>
                <div class="timeline-desc">
                  <span class="desc-text">{{ item.record }}</span>
                </div>

                <!-- 还款信息卡片 -->

              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'OrderTimeline',
  props: {
    // 订单编号
    orderSn: {
      type: String,
      required: true
    },
    orderState: {
      type: String,
      required: true
    },
    // 兼容旧接口：不再使用导出
    showExport: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      orderTimeList: [],
      repayTimeList: [],
      loading: false,
      error: null,
      currentStatus: ""
    };
  },
  watch: {
    // 监听订单编号变化，重新获取数据
    orderSn: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchData();
        }
      }
    },
    // 添加对 orderState 的监听
    orderState: {
      handler(newVal) {
        if (newVal !== undefined && newVal !== null) {
          // 当 orderState 更新时，重新获取审核信息
          this.getReviewInfo();
        }
      }
    }
  },
  methods: {


// 优化后的代码:
    getReviewInfo() {
      // 检查 orderState 是否为指定的状态值之一 (0, 1, 7)
      const validStates = ['0', '1', '7']; // 字符串数组匹配 prop 类型
      if (validStates.includes(this.orderState.toString())) {
        this.getOrderReviewInfo();
      }
    },


    // 移除导出与复制功能（按需保留刷新）
    // 获取所有数据
    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        await Promise.all([
          this.getOrderTime(),
          this.getOrderRepaymentTime(),
          this.getReviewInfo()
        ]);
      } catch (error) {
        console.error('获取数据失败:', error);
        this.error = error.message || '未知错误';
      } finally {
        this.loading = false;
      }
    },
    //获取订单的审核进度信息
    getOrderReviewInfo() {
      this.$http.post(`/orderReviewer/${this.orderSn}?orderState=${this.orderState}`).then(res => {
        this.currentStatus = res.data
      })
    },
    // 获取订单时间线
    async getOrderTime() {
      try {
        const response = await this.$http.post(`/orderTime/${this.orderSn}`);
        const data = response.data;
        this.orderTimeList = data.map(item => ({
          ...item,
          time: this.$formatTimeNew(item.time)
        })).reverse();
      } catch (error) {
        console.error('获取订单时间线失败:', error);
        throw error;
      }
    },

    // 获取订单还款记录
    async getOrderRepaymentTime() {
      try {
        const response = await this.$http.post(`/orderRepayment/${this.orderSn}`);
        const data = response.data;
        this.repayTimeList = data.map(item => ({
          ...item,
          time: this.$formatTimeNew(item.repaymentTime),
          // 保持与原有数据结构一致
          style: this.getRepaymentStyle(item),
          record: item.record || this.generateRepaymentRecord(item)
        })).reverse();
      } catch (error) {
        console.error('获取还款记录失败:', error);
        // 不抛出错误，因为还款记录可能不存在
        this.repayTimeList = [];
      }
    },

    // 生成还款记录描述
    generateRepaymentRecord(item) {
      if (item.repaymentStatus === '还款完成') {
        return `本次订单还款完成，已还款:${item.amount}元`;
      } else if (item.repaymentStatus === '部分还款') {
        return `本次订单部分还款，已还款:${item.amount}元`;
      }
      return `订单还款状态:${item.repaymentStatus}`;
    },
    // 获取审核阶段描述文本
    getAuditStageText(type) {
      const stageMap = {
        'pending_first_audit': '初审',
        'pending_second_audit': '复审',
        'pending_final_audit': '终审',
        'pending_audit': '审核'
      };
      return stageMap[type] || '审核';
    },
    // 获取还款样式
    getRepaymentStyle(item) {
      return item.style || '还款记录';
    },

    // 从内容中提取审核信息
    extractAuditInfo(content, type) {
      if (!content) return '';

      if (type === '审核人') {
        const match = content.match(/审核人:([^,]+)/);
        return match ? match[1].trim() : '';
      }

      if (type === '审核意见') {
        const match = content.match(/审核意见:([^,]+)/);
        return match ? match[1].trim() : '无';
      }

      return '';
    },

    // 从还款记录中提取金额
    extractAmount(record) {
      if (!record) return null;
      const match = record.match(/已还款:([0-9.]+)元/);
      return match ? parseFloat(match[1]) : null;
    },

    // 从还款记录中提取支付方式
    extractPaymentMethod(record) {
      if (!record) return null;
      if (record.includes('银行卡')) return '银行卡支付';
      if (record.includes('支付宝')) return '支付宝支付';
      if (record.includes('微信')) return '微信支付';
      return null;
    },

    // 获取订单操作节点颜色
    getColor(item) {
      const type = item.operateType;
      if (type === '交易完成') return '#07c160';
      if (type === '已收货') return '#07c160';
      if (type === '发货') return '#1989fa';
      if (type === '审核通过') return '#07c160';
      if (type === '终审') return '#ff9c19';
      if (type === '复审') return '#ff9c19';
      if (type === '初审') return '#ff9c19';
      if (type === '创建订单') return '#1989fa';
      return '#969799';
    },

    // 获取状态标签类名
    getStatusClass(operateType) {
      if (operateType === '交易完成') return 'status-success';
      if (operateType === '已收货') return 'status-success';
      if (operateType === '发货') return 'status-info';
      if (operateType === '审核通过') return 'status-success';
      if (operateType === '终审') return 'status-warning';
      if (operateType === '复审') return 'status-warning';
      if (operateType === '初审') return 'status-warning';
      if (operateType === '创建订单') return 'status-info';
      return 'status-default';
    },

    // 获取还款信息节点颜色
    getRepayColor(item) {
      const style = item.style;
      if (style === '还款完成') return '#07c160';
      if (style === '部分还款') return '#ff9c19';
      if (style === '欠款') return '#ff4444';
      return '#1989fa';
    },

    // 获取还款状态标签类名
    getRepayStatusClass(style) {
      if (style === '还款完成') return 'status-success';
      if (style === '部分还款') return 'status-warning';
      if (style === '欠款') return 'status-danger';
      return 'status-info';
    }
  }
}
</script>

<style scoped>
.status-scroll-alert {
  margin-bottom: 16px;
  overflow: hidden;
}

.status-scroll-alert ::v-deep .el-alert__content {
  width: 100%;
}

.custom-scroll-alert {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px 10px;
  background-color: #fdf6ec;
  border: 1px solid #ffe7ba;
  border-radius: 8px;
  overflow: hidden;
}

.alert-icon {
  margin-right: 12px;
  color: #ff9c19;
  font-size: 16px;
}

.scrolling-text-container {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.scrolling-text {
  display: inline-block;
  padding-left: 100%;
  animation: scroll-left 15s linear infinite;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}


.order-timeline-container {
  padding: 0;
  background: transparent;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

/* 移除工具栏样式，保持版面简洁 */

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  color: #909399;
}

.empty-state i {
  font-size: 22px;
  margin-bottom: 6px;
  color: #c0c4cc;
}

.timeline-section {
  margin-bottom: 20px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
}

.timeline-section:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f5f5f5;
}

.title-with-icon {
  display: flex;
  align-items: center;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.icon {
  margin-right: 8px;
  font-size: 16px;
  color: #1989fa;
}

.record-count {
  font-size: 12px;
  color: #999;
}

/* 竖向时间线样式 */
.vertical-timeline {
  position: relative;
  padding-left: 20px;
}

.vertical-timeline::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ebedf0;
}

.timeline-item {
  position: relative;
  padding-bottom: 16px;
  display: flex;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -20px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  z-index: 1;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #ebedf0;
}

.timeline-item.first-item .timeline-dot {
  width: 14px;
  height: 14px;
  left: -21px;
  box-shadow: 0 0 0 2px #1989fa;
}

.timeline-content {
  flex: 1;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: box-shadow .2s ease, transform .2s ease;
}

.timeline-item.first-item .timeline-content {
  background: #f0f9eb;
}

.timeline-content:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, .06);
  transform: translateY(-1px);
}

.timeline-time {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}

.timeline-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.operate-type {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.status-tag {
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 12px;
}

.status-success {
  color: #07c160;
  background: #f0f9eb;
}

.status-warning {
  color: #ff9c19;
  background: #fdf6ec;
}

.status-danger {
  color: #ff4444;
  background: #fef0f0;
}

.status-info {
  color: #1989fa;
  background: #ecf5ff;
}

.status-default {
  color: #969799;
  background: #f8f9fa;
}

.timeline-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.desc-text {
  flex: 1;
  margin-right: 8px;
  word-break: break-word;
}

.copy-icon {
  color: #909399;
  cursor: pointer;
  transition: color .2s ease;
}

.copy-icon:hover {
  color: #606266;
}

/* 还款信息卡片样式 */
.repayment-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  margin-top: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.card-header i {
  margin-right: 6px;
  color: #1989fa;
}

.card-body {
  padding: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  color: #909399;
  font-size: 13px;
}

.info-value {
  font-size: 13px;
  font-weight: 500;
}

.amount {
  color: #f56c6c;
  font-size: 14px;
}

.payment-method {
  display: flex;
  align-items: center;
  color: #606266;
}

.payment-method i {
  margin-right: 4px;
  color: #1989fa;
}

/* 加载和错误状态 */
.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #999;
}

.loading-state i {
  font-size: 24px;
  margin-bottom: 8px;
  color: #1989fa;
}

.error-state i {
  font-size: 24px;
  margin-bottom: 8px;
  color: #ff4444;
}

.error-state p {
  margin-bottom: 12px;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .timeline-section {
    padding: 12px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .record-count {
    margin-top: 4px;
  }

  .timeline-title {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-tag {
    margin-top: 4px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-value {
    margin-top: 4px;
  }
}
</style>



// WEBPACK FOOTER //
// orderTime.vue?f5602bee